import { useState, lazy, Suspense } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { usePermission } from "../../hooks/usePermission";
import {
  BarChart2, FileText, Package, DollarSign, ArrowLeftRight,
  Warehouse, Users, Truck, TrendingUp, UserCheck, Mail,
} from "lucide-react";

const ReportSales          = lazy(() => import("./ReportSales"));
const ReportAccounts       = lazy(() => import("./ReportAccounts"));
const ReportCOGS           = lazy(() => import("./ReportCOGS"));
const ReportExpenses       = lazy(() => import("./ReportExpenses"));
const ReportTransfers      = lazy(() => import("./ReportTransfers"));
const ReportWarehouse      = lazy(() => import("./ReportWarehouse"));
const ReportCustomers      = lazy(() => import("./ReportCustomers"));
const ReportVendors        = lazy(() => import("./ReportVendors"));
const ReportProfitLoss     = lazy(() => import("./ReportProfitLoss"));
const ReportUsers          = lazy(() => import("./ReportUsers"));
const ReportEmailScheduler = lazy(() => import("./ReportEmailScheduler"));

const TABS = [
  { key: "sales",      label: "گزارش فروشات",    icon: BarChart2,      color: "#10b981" },
  { key: "accounts",   label: "گزارش حساب‌ها",    icon: FileText,       color: "#3b82f6" },
  { key: "cogs",       label: "قیمت تمام شده",    icon: Package,        color: "#f59e0b" },
  { key: "expenses",   label: "گزارش مصارف",      icon: DollarSign,     color: "#ef4444" },
  { key: "transfers",  label: "گزارش انتقالات",    icon: ArrowLeftRight, color: "#06b6d4" },
  { key: "warehouse",  label: "گزارش گدام",       icon: Warehouse,      color: "#8b5cf6" },
  { key: "customers",  label: "گزارش مشتریان",    icon: Users,          color: "#ec4899" },
  { key: "vendors",    label: "گزارش فروشندگان",   icon: Truck,          color: "#f97316" },
  { key: "profitloss", label: "سود و زیان",       icon: TrendingUp,     color: "#14b8a6" },
  { key: "users",      label: "گزارش کاربران",    icon: UserCheck,      color: "#6366f1" },
  { key: "email",      label: "ارسال گزارشات",    icon: Mail,           color: "#64748b" },
];

export default function Reports() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const canView = usePermission("reports", "view");
  const [activeTab, setActiveTab] = useState("sales");

  if (!canView) {
    return (
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        height: 300, color: T.subtext, fontSize: 15,
        fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
      }}>
        دسترسی به گزارشات ندارید
      </div>
    );
  }

  const activeInfo = TABS.find(t => t.key === activeTab)!;

  const renderTab = () => {
    switch (activeTab) {
      case "sales":      return <ReportSales />;
      case "accounts":   return <ReportAccounts />;
      case "cogs":       return <ReportCOGS />;
      case "expenses":   return <ReportExpenses />;
      case "transfers":  return <ReportTransfers />;
      case "warehouse":  return <ReportWarehouse />;
      case "customers":  return <ReportCustomers />;
      case "vendors":    return <ReportVendors />;
      case "profitloss": return <ReportProfitLoss />;
      case "users":      return <ReportUsers />;
      case "email":      return <ReportEmailScheduler />;
      default:           return null;
    }
  };

  return (
    <div style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>

      {/* Header */}
      <div style={{
        marginBottom: 22, padding: "16px 20px",
        background: dark
          ? "linear-gradient(135deg,rgba(59,130,246,0.1),rgba(16,185,129,0.06))"
          : "linear-gradient(135deg,#f0f9ff,#f0fdf4)",
        borderRadius: 14, border: `1px solid ${T.cardBorder}`,
        display: "flex", alignItems: "center", gap: 14,
      }}>
        <div style={{
          width: 42, height: 42, borderRadius: 11, flexShrink: 0,
          background: `linear-gradient(135deg,${activeInfo.color},${activeInfo.color}99)`,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: `0 4px 12px ${activeInfo.color}40`,
        }}>
          <activeInfo.icon size={19} color="#fff" />
        </div>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: T.text }}>مرکز گزارشات</h2>
          <p style={{ margin: 0, fontSize: 11, color: T.subtext, marginTop: 2 }}>{activeInfo.label}</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: "flex", gap: 4, flexWrap: "wrap",
        marginBottom: 20, padding: "7px",
        background: dark ? "rgba(255,255,255,0.02)" : "#f8fafc",
        borderRadius: 13, border: `1px solid ${T.cardBorder}`,
      }}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.key;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
              display: "flex", alignItems: "center", gap: 6,
              padding: "7px 13px", borderRadius: 9, border: "none",
              cursor: "pointer", fontSize: 12, fontWeight: active ? 700 : 500,
              fontFamily: "'Vazirmatn','Segoe UI',sans-serif",
              transition: "all 0.18s",
              background: active ? `linear-gradient(135deg,${tab.color},${tab.color}cc)` : "transparent",
              color: active ? "#fff" : T.subtext,
              boxShadow: active ? `0 3px 10px ${tab.color}40` : "none",
            }}>
              <Icon size={13} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <Suspense fallback={
        <div style={{ textAlign: "center", padding: 60, color: T.subtext, fontSize: 13 }}>
          در حال بارگذاری گزارش...
        </div>
      }>
        {renderTab()}
      </Suspense>
    </div>
  );
}